import * as THREE from 'three';
import { fxSettings } from './settings.ts';

/** Where the sprite lives offstage, by side. */
const OFFSTAGE_X = { left: -4, right: 4 } as const;

export type PaperSpriteOptions = {
  id: string;
  /** Big readable label drawn onto the placeholder quad (e.g. "host", "child"). */
  label: string;
  /** Body color of the placeholder rectangle. Used as the background color
   *  before any image loads, and as the fallback if image load fails. */
  color: number;
  /** Optional image URL to paint as the sprite background. Loaded async; the
   *  procedural color+label is shown until the image arrives. */
  imageUrl?: string;
  /** Optional alternate image URL. Loaded alongside imageUrl. Swap to
   *  it via setUseAlt(true) — used by the narrator-puppeteer flow to
   *  briefly replace the player's art with the greyman placeholder. */
  altImageUrl?: string;
  /** How to fit the image into the sprite quad.
   *  - 'cover'   (default): scale to fully cover, crop overflow.
   *  - 'contain': scale to fit entirely, transparent letterbox around it. */
  fit?: 'cover' | 'contain';
  /** Resting on-stage X position. Y/Z come from defaults unless overridden. */
  homeX: number;
  homeY?: number;
  homeZ?: number;
  width?: number;
  height?: number;
  /** Whether this sprite participates in idle sway (paper-on-stick breath).
   *  Characters: true. Environmental props (trees, rocks, castles): false —
   *  trees don't levitate. Default true. */
  idle?: boolean;
  /** Mirror the source image horizontally on the canvas. Useful when
   *  the artwork faces the "wrong" way for a given home position. The
   *  label (when shown) is NOT mirrored. Default false. */
  flipX?: boolean;
  /** Render the mesh fully transparent — material opacity 0 — while
   *  keeping the geometry in the scene graph so the screen rect is
   *  still computable for hotspot tracking. Used to anchor an
   *  invisible click area over a stage region without painting a
   *  visible quad. Default false. */
  invisible?: boolean;
};

/**
 * Stand-in for a hand-drawn paper character. Renders as a colored plane with
 * the character's name baked into a CanvasTexture so the dev/playtest sees
 * "host" / "child" / "crone" labels until real art is dropped in. Same API
 * will work once real PNGs replace the procedural texture.
 */
export class PaperSprite {
  readonly mesh: THREE.Mesh;
  readonly id: string;
  private homePos: THREE.Vector3;
  private targetPos: THREE.Vector3;
  private targetRotZ = 0;
  private targetScale = 1;
  private exiting: 'left' | 'right' | null = null;
  private talking = false;
  private talkPhase = Math.random() * Math.PI * 2;
  private readonly idleEnabled: boolean;
  // Idle sway phases — randomized per-sprite so a stage of paper figures
  // breathes out of sync, like real puppets on sticks held by tired hands.
  private idleY = Math.random() * Math.PI * 2;
  private idleR = Math.random() * Math.PI * 2;
  private idleYRate = 0.7 + Math.random() * 0.5;   // ~0.7–1.2 rad/s
  private idleRRate = 0.4 + Math.random() * 0.4;   // ~0.4–0.8 rad/s

  // Cached so repaint() can re-render the texture when fxSettings.showLabels
  // toggles. The image (when loaded) is kept around so we never have to
  // re-fetch it on a repaint.
  private readonly opts: PaperSpriteOptions;
  private readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;
  private readonly texture: THREE.CanvasTexture;
  private readonly meshAspect: number;
  private mainImage: HTMLImageElement | null = null;
  private altImage: HTMLImageElement | null = null;
  private usingAlt = false;
  private get loadedImage(): HTMLImageElement | null {
    return this.usingAlt ? this.altImage : this.mainImage;
  }
  /** Alpha-tight content rect in mesh-local coords (Z=0). Recomputed
   *  after each repaint by sampling the canvas alpha channel — the
   *  smallest box enclosing all non-transparent pixels. Null while
   *  the canvas is fully transparent (image still loading). Used by
   *  Stage's hotspot screen-rect helpers so click targets hug the
   *  visible silhouette instead of the full quad's letterbox. */
  private contentBox: THREE.Box3 | null = null;
  private readonly meshWidth: number;
  private readonly meshHeight: number;
  /** Max canvas dimension. High enough to keep upscaled source art crisp,
   *  low enough not to thrash GPU memory across ~15 sprites. */
  private static readonly MAX_CANVAS_DIM = 2048;
  private static readonly PLACEHOLDER_BASE_RES = 256;

  constructor(opts: PaperSpriteOptions) {
    this.id = opts.id;
    this.opts = opts;
    this.idleEnabled = opts.idle ?? true;
    const w = opts.width ?? 0.9;
    const h = opts.height ?? 1.35;
    this.meshWidth = w;
    this.meshHeight = h;
    this.meshAspect = w / h;

    // Initial canvas — small placeholder size, aspect-matched to the
    // mesh. When an image loads (resizeCanvasForImage), we'll grow this
    // to the source image's native resolution so the texture isn't
    // downsampled before it ever hits the GPU.
    this.canvas = document.createElement('canvas');
    this.sizeCanvasFromBase(PaperSprite.PLACEHOLDER_BASE_RES);
    this.ctx = this.canvas.getContext('2d')!;

    this.texture = new THREE.CanvasTexture(this.canvas);
    this.texture.colorSpace = THREE.SRGBColorSpace;
    this.texture.anisotropy = 8;        // stay sharp at oblique angles
    const mat = new THREE.MeshBasicMaterial({
      map: this.texture,
      transparent: true,
      side: THREE.DoubleSide,
      // Color is multiplied with the texture sample — darken every
      // sprite (characters + props) to 50% of the source art.
      color: 0x808080,
      // Invisible-prop mode: opacity 0 hides the visual entirely while
      // the mesh still exists in the scene graph for screen-rect
      // projection (so a hotspot can click an invisible region).
      opacity: opts.invisible ? 0 : 1,
      depthWrite: opts.invisible ? false : true,
    });
    this.mesh = new THREE.Mesh(new THREE.PlaneGeometry(w, h), mat);
    this.mesh.name = `sprite:${opts.id}`;

    this.repaint();   // initial draw — placeholder + (optional) label

    // Async-paint the actual image once it arrives. Resize the canvas
    // to capture the image at native resolution, then repaint.
    const loadInto = (
      url: string,
      assign: (img: HTMLImageElement) => void,
      isActive: () => boolean,
    ) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        assign(img);
        if (isActive()) {
          this.resizeCanvasForImage(img);
          this.repaint();
        }
      };
      img.onerror = () => console.warn(`[paper-sprite] failed to load ${url}`);
      img.src = url;
    };
    if (opts.imageUrl) {
      loadInto(opts.imageUrl, img => { this.mainImage = img; }, () => !this.usingAlt);
    }
    if (opts.altImageUrl) {
      loadInto(opts.altImageUrl, img => { this.altImage = img; }, () => this.usingAlt);
    }

    this.homePos = new THREE.Vector3(
      opts.homeX,
      opts.homeY ?? h / 2 - 0.7,
      opts.homeZ ?? -0.4,
    );
    this.targetPos = this.homePos.clone();
    this.mesh.position.copy(this.targetPos);
    this.mesh.visible = false;
  }

  /** Set canvas dimensions from a `base` longer-edge value, scaled by
   *  the mesh aspect so the canvas matches the rendered quad's shape
   *  (no post-stretch). */
  private sizeCanvasFromBase(base: number) {
    if (this.meshAspect >= 1) {
      this.canvas.width = Math.round(base);
      this.canvas.height = Math.round(base / this.meshAspect);
    } else {
      this.canvas.width = Math.round(base * this.meshAspect);
      this.canvas.height = Math.round(base);
    }
  }

  /** Resize the canvas so that, when the source image is drawn into it
   *  with the chosen fit mode, the image's pixels are preserved at native
   *  resolution (no pre-GPU downsample). Caps at MAX_CANVAS_DIM. */
  private resizeCanvasForImage(img: HTMLImageElement) {
    const imgAspect = img.width / img.height;
    let cw: number, ch: number;
    if (imgAspect >= this.meshAspect) {
      // Image is wider than (or as wide as) the mesh — fit by width.
      cw = img.width;
      ch = Math.round(cw / this.meshAspect);
    } else {
      // Image is taller — fit by height.
      ch = img.height;
      cw = Math.round(ch * this.meshAspect);
    }
    const longest = Math.max(cw, ch);
    if (longest > PaperSprite.MAX_CANVAS_DIM) {
      const scale = PaperSprite.MAX_CANVAS_DIM / longest;
      cw = Math.round(cw * scale);
      ch = Math.round(ch * scale);
    }
    this.canvas.width = cw;
    this.canvas.height = ch;
  }

  /** Redraw the canvas texture: placeholder (or loaded image) + the
   *  optional debug label. Called on construction, on image load, and
   *  by Stage when the fxSettings.showLabels checkbox flips. */
  repaint() {
    const { canvas, ctx, opts, loadedImage } = this;
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    if (loadedImage) {
      const sx = canvas.width / loadedImage.width;
      const sy = canvas.height / loadedImage.height;
      // cover → max of the two scales (image overflows, sides crop).
      // contain → min of the two scales (image fits entirely, letterbox).
      const scale = (opts.fit ?? 'cover') === 'cover' ? Math.max(sx, sy) : Math.min(sx, sy);
      const dw = loadedImage.width * scale;
      const dh = loadedImage.height * scale;
      const dx = (canvas.width - dw) / 2;
      const dy = (canvas.height - dh) / 2;
      if (opts.flipX) {
        // Mirror about the canvas Y axis: scale(-1,1) inverts X, then
        // drawImage at -(dx + dw) lands the image at the same on-screen
        // X range as the unflipped case but with pixels reversed.
        ctx.save();
        ctx.scale(-1, 1);
        ctx.drawImage(loadedImage, -(dx + dw), dy, dw, dh);
        ctx.restore();
      } else {
        ctx.drawImage(loadedImage, dx, dy, dw, dh);
      }
    } else {
      const hex = `#${opts.color.toString(16).padStart(6, '0')}`;
      ctx.fillStyle = hex;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.strokeStyle = 'rgba(0,0,0,0.6)';
      ctx.lineWidth = 8;
      ctx.strokeRect(4, 4, canvas.width - 8, canvas.height - 8);
    }

    if (fxSettings.showLabels) {
      ctx.font = `bold ${Math.round(canvas.height * 0.12)}px ui-monospace, monospace`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.lineWidth = 8;
      ctx.strokeStyle = '#000000';
      ctx.strokeText(opts.label, canvas.width / 2, canvas.height / 2);
      ctx.fillStyle = '#ffffff';
      ctx.fillText(opts.label, canvas.width / 2, canvas.height / 2);
    }

    this.texture.needsUpdate = true;
    this.recomputeContentBox();
  }

  /** Scan the canvas alpha channel and store the tightest non-
   *  transparent rect as a mesh-local Box3. Cheap (one canvas read
   *  per repaint, repaints are rare) and lets hotspot click targets
   *  hug the visible silhouette instead of the full quad. */
  private recomputeContentBox() {
    const W = this.canvas.width;
    const H = this.canvas.height;
    let data: Uint8ClampedArray;
    try {
      data = this.ctx.getImageData(0, 0, W, H).data;
    } catch {
      // Cross-origin or other read failure — fall back to the full
      // quad so hotspots still work, just at the original loose size.
      this.contentBox = this.fullQuadBox();
      return;
    }
    let minX = W, minY = H, maxX = -1, maxY = -1;
    // Step by 2 in both axes — a 2× speed-up that doesn't change the
    // bbox at sprite resolutions (a 1px off in either direction is
    // imperceptible for click rects).
    for (let y = 0; y < H; y += 2) {
      const rowBase = y * W * 4;
      for (let x = 0; x < W; x += 2) {
        if (data[rowBase + x * 4 + 3] > 32) {
          if (x < minX) minX = x;
          if (x > maxX) maxX = x;
          if (y < minY) minY = y;
          if (y > maxY) maxY = y;
        }
      }
    }
    if (maxX < 0) {
      // Fully transparent canvas (e.g. before image loads, or invisible
      // sprite that hasn't filled with placeholder color). Keep the
      // last known box rather than nulling — avoids hotspot flicker
      // when an alt-image swap repaints empty for one frame.
      if (!this.contentBox) this.contentBox = this.fullQuadBox();
      return;
    }
    // Canvas (origin top-left, Y down) → mesh-local (centered, Y up).
    const w = this.meshWidth;
    const h = this.meshHeight;
    const uMin = minX / W;
    const uMax = (maxX + 1) / W;
    const vMin = 1 - (maxY + 1) / H;
    const vMax = 1 - minY / H;
    this.contentBox = new THREE.Box3(
      new THREE.Vector3((uMin - 0.5) * w, (vMin - 0.5) * h, 0),
      new THREE.Vector3((uMax - 0.5) * w, (vMax - 0.5) * h, 0),
    );
  }

  private fullQuadBox(): THREE.Box3 {
    return new THREE.Box3(
      new THREE.Vector3(-this.meshWidth / 2, -this.meshHeight / 2, 0),
      new THREE.Vector3( this.meshWidth / 2,  this.meshHeight / 2, 0),
    );
  }

  /** Tightest world-space AABB enclosing the visible (non-transparent)
   *  pixels of this sprite. Returns null until the canvas has at
   *  least one repaint with content. Stage uses this for hotspot
   *  screen-rect projection so click targets follow the visible art. */
  getWorldContentBox(): THREE.Box3 | null {
    if (!this.contentBox) return null;
    this.mesh.updateWorldMatrix(true, false);
    return this.contentBox.clone().applyMatrix4(this.mesh.matrixWorld);
  }

  /** Slide on from the named side and settle at home. */
  enter(side: 'left' | 'right' | 'center' = 'center') {
    const startX =
      side === 'left' ? OFFSTAGE_X.left
      : side === 'right' ? OFFSTAGE_X.right
      : this.homePos.x;
    this.mesh.position.set(startX, this.homePos.y, this.homePos.z);
    this.mesh.visible = true;
    this.exiting = null;
    this.targetPos.copy(this.homePos);
    this.targetRotZ = 0;
    this.targetScale = 1;
  }

  /** Slide off toward a side, then auto-hide once we reach it. */
  exit(side: 'left' | 'right' = 'left') {
    this.exiting = side;
    this.targetPos.set(OFFSTAGE_X[side], this.homePos.y, this.homePos.z);
  }

  /** Snap the sprite directly to its home position and make it visible
   *  with NO slide animation. Used by the `place` beat to populate
   *  initial scene state behind a closed curtain. Optional x/y override
   *  the home position so a character can spawn somewhere other than
   *  its default. */
  snapVisible(x?: number, y?: number) {
    if (x !== undefined) {
      this.homePos.x = x;
      this.targetPos.x = x;
    }
    if (y !== undefined) {
      this.homePos.y = y;
      this.targetPos.y = y;
    }
    this.mesh.position.copy(this.homePos);
    this.mesh.visible = true;
    this.exiting = null;
    this.targetRotZ = 0;
    this.targetScale = 1;
    this.mesh.rotation.z = 0;
    this.mesh.scale.setScalar(1);
  }

  /** Hide the sprite and reset its target so the next enter starts
   *  cleanly. Used by Stage.clearStage() before a place change. */
  hide() {
    this.mesh.visible = false;
    this.exiting = null;
    this.targetPos.copy(this.homePos);
    this.mesh.position.copy(this.homePos);
  }

  /** Swap between the main image and the alt image (if both are
   *  configured). Called by Game's narrator path to flip the player
   *  sprite to the greyman placeholder while the host speaks. */
  setUseAlt(useAlt: boolean) {
    if (this.usingAlt === useAlt) return;
    this.usingAlt = useAlt;
    const img = this.loadedImage;
    if (img) this.resizeCanvasForImage(img);
    this.repaint();
  }

  /** Bumped on every setImages call. Each Image.onload captures the
   *  version it was started with and bails if a newer setImages has
   *  since superseded it — kills a real bug where a slow-loading
   *  previous-room image would arrive AFTER the new room's image and
   *  overwrite it (e.g. swamp's frog_front paints over the witch). */
  private imageLoadVersion = 0;

  /** Process-wide cache of fully-decoded images keyed by URL. Once an
   *  image has loaded once, subsequent setImages calls assign it
   *  synchronously — no flicker, no async wait. The parser engine
   *  primes this cache at startup with every per-room override URL
   *  via PaperSprite.preload(), so the first cross-room transition is
   *  also instant. */
  private static imageCache = new Map<string, HTMLImageElement>();

  /** Eagerly fetch + decode a list of image URLs into the static
   *  cache. Resolves once every URL has either loaded or errored
   *  (errors don't reject — we don't block the play on them). */
  static preload(urls: string[]): Promise<void> {
    console.log(`[paper-sprite] preload starting: ${urls.length} urls`, urls);
    const promises = urls.map(url => {
      const cached = PaperSprite.imageCache.get(url);
      if (cached?.complete) return Promise.resolve();
      return new Promise<void>(resolve => {
        const img = new Image();
        img.onload = () => {
          PaperSprite.imageCache.set(url, img);
          console.log(`[paper-sprite] preload OK: ${url} (${img.naturalWidth}×${img.naturalHeight})`);
          resolve();
        };
        img.onerror = () => {
          console.warn(`[paper-sprite] preload FAILED: ${url}`);
          resolve();
        };
        img.src = url;
      });
    });
    return Promise.all(promises).then(() => {
      console.log(`[paper-sprite] preload complete. cache has ${PaperSprite.imageCache.size} entries`);
    });
  }

  /** Lookup a cached HTMLImageElement by URL. Returns null if the URL
   *  hasn't been loaded yet (call PaperSprite.preload first). */
  static getCached(url: string): HTMLImageElement | null {
    const img = PaperSprite.imageCache.get(url);
    return img?.complete ? img : null;
  }

  /** Synchronously install fully-loaded image elements into the main
   *  and alt slots, reset usingAlt to false, and repaint once. This is
   *  the bulletproof override path used by the parser engine — caller
   *  resolves URLs to HTMLImageElement BEFORE calling this, so there
   *  is no async window, no version race, no stale-state edge case.
   *
   *  Pass null to clear a slot. */
  setImagesSync(main: HTMLImageElement | null, alt: HTMLImageElement | null) {
    // Bump version to invalidate any in-flight async loads from the
    // string-URL setImages path, in case those are still pending.
    this.imageLoadVersion++;
    this.mainImage = main;
    this.altImage = alt;
    this.usingAlt = false;  // always start from main on override
    if (main) this.resizeCanvasForImage(main);
    this.repaint();
  }

  /** Replace the sprite's main and (optionally) alt image at runtime.
   *  Used by the parser engine to apply per-room sprite overrides.
   *
   *  Cache-first: if the URL has already been loaded (either by an
   *  earlier setImages call or by PaperSprite.preload), the assign +
   *  repaint happens synchronously in this function and the player
   *  never sees a placeholder flash or a stale frame. Cache misses
   *  fall back to the async <Image>.onload path, which still updates
   *  the slot when the bytes arrive (and writes the loaded element
   *  into the cache for next time).
   *
   *  Async loads carry an imageLoadVersion captured at call time — a
   *  newer setImages will bump the version, and the older load's
   *  onload bails when it sees the mismatch. Closes the cross-room
   *  race where a slow previous-room image could arrive AFTER a
   *  newer one and overwrite it. */
  setImages(opts: { main?: string | null; alt?: string | null }) {
    this.imageLoadVersion++;
    const myVersion = this.imageLoadVersion;

    // Update both slots first WITHOUT repainting. Each branch only
    // mutates state. The single unconditional repaint at the end
    // observes the final state (after both main+alt are set and
    // usingAlt is reconciled) and paints the correct image.
    //
    // The previous version repainted INSIDE applyMain/applyAlt and
    // gated those repaints on `usingAlt` — but `usingAlt` could be
    // stale at the time of the first repaint (e.g. main is set sync
    // from cache before alt's null-clear has reset usingAlt). Net:
    // mainImage was assigned to the new image, but the canvas never
    // got a chance to draw it, and the texture stayed on the
    // previous room's last frame.
    if (opts.main !== undefined) {
      if (opts.main === null) {
        this.mainImage = null;
      } else {
        const cached = PaperSprite.imageCache.get(opts.main);
        if (cached?.complete) {
          this.mainImage = cached;
        } else {
          this.scheduleAsyncLoad(opts.main, 'main', myVersion);
        }
      }
    }
    if (opts.alt !== undefined) {
      if (opts.alt === null) {
        this.altImage = null;
        if (this.usingAlt) this.usingAlt = false; // fall back to main
      } else {
        const cached = PaperSprite.imageCache.get(opts.alt);
        if (cached?.complete) {
          this.altImage = cached;
        } else {
          this.scheduleAsyncLoad(opts.alt, 'alt', myVersion);
        }
      }
    }

    // Single unconditional repaint. Picks main or alt based on the
    // FINAL usingAlt state, resizes the canvas to the chosen image,
    // and writes pixels. If the slot is empty (e.g. async still in
    // flight) the placeholder color is painted — better than leaving
    // the canvas with the previous room's last frame.
    this.finalizeRepaint();
  }

  /** Async-load fallback used when a URL isn't yet in the static cache.
   *  Bails on completion if a newer setImages has bumped the version
   *  in the meantime (cross-room race protection). On success, caches
   *  the loaded image so future setImages with this URL are sync. */
  private scheduleAsyncLoad(url: string, slot: 'main' | 'alt', myVersion: number) {
    const img = new Image();
    img.onload = () => {
      PaperSprite.imageCache.set(url, img);
      if (myVersion !== this.imageLoadVersion) return;
      if (slot === 'main') this.mainImage = img;
      else this.altImage = img;
      this.finalizeRepaint();
    };
    img.onerror = () => console.warn(`[paper-sprite] failed to load ${url}`);
    img.src = url;
  }

  /** Resize the canvas to the currently-active image (main or alt
   *  depending on `usingAlt`) and repaint. Used after every setImages
   *  call and after every async load completes. */
  private finalizeRepaint() {
    const active = this.usingAlt ? this.altImage : this.mainImage;
    if (active) this.resizeCanvasForImage(active);
    this.repaint();
  }

  /** Whether the sprite is currently visible on stage. */
  isVisible(): boolean { return this.mesh.visible; }
  /** Make the sprite visible without altering its position. Used by
   *  Game's host-puppeteer swap to restore the host placeholder after
   *  the puppeteer slides back below the stage. */
  show() { this.mesh.visible = true; }

  moveTo(x: number, y?: number) {
    this.targetPos.x = x;
    if (y !== undefined) this.targetPos.y = y;
    this.homePos.x = this.targetPos.x;
    if (y !== undefined) this.homePos.y = this.targetPos.y;
  }

  /** Tilt by degrees around Z (paper-puppet lean). */
  lean(deg: number) {
    this.targetRotZ = (deg * Math.PI) / 180;
  }

  /** Quick wobble — useful for hits, surprises, scared reactions. */
  shake(durationMs = 220) {
    const startMs = performance.now();
    const wobble = () => {
      const t = (performance.now() - startMs) / durationMs;
      if (t >= 1) {
        this.targetRotZ = 0;
        return;
      }
      this.targetRotZ = (Math.sin(t * 30) * (1 - t)) * 0.25;
      requestAnimationFrame(wobble);
    };
    wobble();
  }

  setTalking(on: boolean) {
    this.talking = on;
    if (!on) this.talkPhase = Math.random() * Math.PI * 2;
    // Speaker rim-glow: lift the active speaker's material color toward
    // warm-bright while talking, then drop back to the dim neutral
    // base. The 50%-darkened cast (0x808080) becomes a warmer ~70%
    // (0xb09c80) — reads as a key light hitting the puppet without
    // washing out the paper texture.
    const mat = this.mesh.material as THREE.MeshBasicMaterial;
    mat.color.setHex(on ? 0xb09c80 : 0x808080);
  }

  setLayer(layer: number) {
    this.mesh.layers.set(layer);
  }

  /** Per-frame lerp toward target pose + talking bob. dt in seconds. */
  update(dt: number) {
    const t = Math.min(1, 6.0 * dt);
    this.mesh.position.x += (this.targetPos.x - this.mesh.position.x) * t;
    this.mesh.position.z += (this.targetPos.z - this.mesh.position.z) * t;
    // Y has the bob layered on top of the lerp toward target.
    let baseY = this.mesh.position.y + (this.targetPos.y - this.mesh.position.y) * t;
    const idle = this.idleEnabled && fxSettings.idleSway;
    if (this.talking) {
      this.talkPhase += dt * 12;
      baseY += Math.sin(this.talkPhase) * 0.025;
    } else if (idle) {
      // Slow paper breath — ~12mm bob, much gentler than the talk bob.
      this.idleY += dt * this.idleYRate;
      baseY += Math.sin(this.idleY) * 0.012;
    }
    this.mesh.position.y = baseY;
    this.mesh.rotation.z += (this.targetRotZ - this.mesh.rotation.z) * t;
    if (!this.talking && idle) {
      // ±~0.3° of paper-on-stick wobble, additive on top of the lean.
      this.idleR += dt * this.idleRRate;
      this.mesh.rotation.z += Math.sin(this.idleR) * 0.005;
    }
    this.mesh.scale.setScalar(this.mesh.scale.x + (this.targetScale - this.mesh.scale.x) * t);

    // Auto-hide once we've slid offstage.
    if (this.exiting && Math.abs(this.mesh.position.x - this.targetPos.x) < 0.05) {
      this.mesh.visible = false;
      this.exiting = null;
    }
  }
}
